import { useEffect, useState } from "react";

// Components
import TableLoader from "@Layouts/components/Skeletons/TableLoader";
import OemVolvoRegionStore from "@DataPages/components/MyOEM/Tables/OemVolvoRegionStore";
import OemVolvoEmployees from "@DataPages/components/MyOEM/Tables/OemVolvoEmployees";
import OEMTableTitle from "@/Layouts/components/OEMTableTitle";

import { formatNumberWithCommas } from "@Common/helpers/Modifiers/formatters";

// MUI
import { Grid } from "@mui/material";

// Images
import emptyTable from "@/assets/images/commons/empty_table.png";
import empty from "@/assets/images/commons/empty_table.png";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import { errorLogger } from "@/Common/helpers/logger";

const matchReferences = (references = []) => {
	const referencesMap = new Map();

	if (references.length) {
		references
			.filter((reference) => reference?.metrics?.length)
			.forEach((reference) => {
				const referenceData = referencesMap.get(reference._id) || {
					_id: reference._id,
				};

				reference?.metrics?.forEach((metric) => {
					referenceData[metric.metricName] = metric?.metricValue || 0.0;
				});

				delete referenceData?.metrics;
				delete referenceData?.user;
				referencesMap.set(reference._id, referenceData);
			});
	}

	return Array.from(referencesMap.values());
};

const getReferenceColumns = (references = null) => {
	const columns = [];
	if (references) {
		Object.keys(references).forEach((key) => {
			if (key) {
				columns.push({
					field: key,
					headerName: key === "Reference" ? "Reference" : key,
					type: key === "Reference" ? "string" : "number",
					hide: Boolean(key !== "Overall NPS" && key !== "Overall Satisfaction" && key !== "Reference"),
					sortable: true,
					headerClassName: Boolean(key === "Reference") ? "show-as-column" : "show-as-column header-text-right",
					cellClassName: `table-small-cell`,
					width: key === "Reference" ? "200" : "150",
					align: key === "Reference" ? "left" : "right",
					renderCell: (param) => {
						if (param.value && isNaN(param.value)) {
							return param.value;
						}
						try {
							return formatNumberWithCommas(Number(param.value).toFixed(2).toString());
						} catch (error) {
							errorLogger(error);
						}
						return "Unknown";
					},
				});
			}
		});
	}

	return columns;
};

const getEmployeeColumns = (employees = null) => {
	const columns = [];
	if (employees) {
		Object.keys(employees).forEach((key) => {
			columns.push({
				field: key,
				headerName: key,
				hide: Boolean(key !== "Overall NPS" && key !== "Overall Satisfaction" && key !== "Associate" && key !== "Sample Size"),
				sortable: true,
				type: key === "Associate" ? "string" : "number",
				headerClassName: key === "Associate" ? "show-as-column" : "show-as-column header-text-right",
				cellClassName: `table-small-cell`,
				width: key === "Associate" ? "200" : "150",
				align: key === "Associate" ? "left" : "right",
				renderCell: (param) => {
					if (param.value && isNaN(param.value)) {
						return param.value;
					}
					try {
						if (param.value) {
							if (param.field === "responses") {
								return formatNumberWithCommas(Number(param.value).toFixed(0).toString());
							} else {
								return formatNumberWithCommas(Number(param.value).toFixed(2).toString());
							}
						}
						return Number(0).toFixed(2);
					} catch (error) {
						errorLogger(error);
					}

					return "Unknown";
				},
			});
		});

		let first = null;
		columns?.forEach((element, key) => {
			if (element.field === "fullName") {
				first = element;
				delete columns[key];
			}
		});

		if (first) {
			columns.unshift(first);
		}
	}

	return columns;
};

export default function VolvoDataContent(props) {
	const [reference, setReference] = useState([]);
	const [referenceColumns, setReferenceColumns] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [employeesColumns, setEmployeesColumns] = useState([]);

	useEffect(() => {
		let referenceToTable = [];
		let columnsReferenceToTable = [];
		let employeesToTable = [];
		let columnsEmployeesToTable = [];

		if (props.isDataPresent) {
			referenceToTable = matchReferences(props.referenceMetrics || []);
			employeesToTable = matchReferences(props.employeeMetrics || []);

			if (referenceToTable.length) {
				columnsReferenceToTable = getReferenceColumns(referenceToTable[0]);
			}

			if (employeesToTable.length) {
				columnsEmployeesToTable = getEmployeeColumns(employeesToTable[0]);
			}
		}

		setReference(referenceToTable);
		setEmployees(employeesToTable);
		setReferenceColumns(columnsReferenceToTable);
		setEmployeesColumns(columnsEmployeesToTable);

		return () => {
			setReference([]);
			setEmployees([]);
			setReferenceColumns([]);
			setEmployeesColumns([]);
		};
	}, [props]);

	return (
		<>
			{props.loading ? (
				<Grid container item mt={0} rowSpacing={2} columnSpacing={4}>
					<Grid item xs={12}>
						<TableLoader content rows={2} />
					</Grid>
					<Grid item mt={2} xs={12}>
						<TableLoader content rows={7} toolbar buttons={2} />
					</Grid>
				</Grid>
			) : (
				<>
					{props.isDataPresent ? (
						<Grid container item mt={0} rowSpacing={2}>
							<Grid item xs={12} sx={{ minWidth: "100px" }}>
								{OEMTableTitle("Store")}
								<OemVolvoRegionStore columns={referenceColumns} data={reference} />
							</Grid>
							{!!employees?.length && (
								<Grid item xs={12} sx={{ minWidth: "100px", marginTop: "15px" }}>
									{OEMTableTitle("Associates")}
									<OemVolvoEmployees data={employees} columns={employeesColumns} />
								</Grid>
							)}
						</Grid>
					) : (
						<EmptyBody
							image={props.selected ? emptyTable : empty}
							title={props.selected ? "No Performance found for this month" : "Select a month to view their Performance"}
							bodyHeight={"480px"}
							sx={{
								boxShadow: "none !important",
							}}
						/>
					)}
				</>
			)}
		</>
	);
}
